import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { WorkflowStatus } from '../../types'
import { documentWorkflowHistoryService } from '../../services/document-workflow-history.service'
import type { WorkflowHistoryEntry } from './DocumentWorkflowStepper'
import { DocumentWorkflowStepperModal } from './DocumentWorkflowStepperModal'

export interface DocumentDetailWorkflowActionsPayload {
  in_workflow: boolean
  workflow_definition: { id: number; name: string } | null
  current_step?: { id: number; status_id: number; position: number } | null
  available_actions: Array<{ id: number; name: string; end_workflow: boolean; color?: string; text_color?: string; comment_required?: boolean }>
  available_definitions: Array<{ id: number; name: string }>
  user_can_claim: boolean
  /** True when more than one user can decide on this step; show claim so one can take it. */
  has_multiple_assignees?: boolean
  /** True when current user is in assigned users/groups for the current step (can see and run actions). */
  user_can_perform_actions?: boolean
  steps?: Array<{ id: number; position: number; status_id: number; name?: string }>
  /** Usernames assigned to the current step (who is waiting on this step). */
  current_step_assignee_usernames?: string[]
  /** Group/role names assigned to the current step. */
  current_step_assignee_group_names?: string[]
}

export interface DocumentDetailWorkflowProps {
  documentId: number
  documentWorkflowStatus: WorkflowStatus | null
  workflowAvailableActions: DocumentDetailWorkflowActionsPayload | null
  workflowStatuses: WorkflowStatus[]
  workflowStatusId: number | null
  workflowHistory?: WorkflowHistoryEntry[]
  /** When set, clicking an action in the stepper runs this (e.g. advance via API) and closes the stepper. */
  onWorkflowAction?: (actionId: number, comment?: string) => void
  /** Controlled stepper modal (e.g. from Actions menu); when set, badge click and Actions open same modal. */
  showStepperModal?: boolean
  setShowStepperModal?: (show: boolean) => void
  onClaim: () => void
  onUnclaim: () => void
  claimedById: number | null
  isClaimedByMe: boolean
  claimSubmitting: boolean
  userCanEdit: boolean
  claimedByUsername: string | null
}

export const DocumentDetailWorkflow: React.FC<DocumentDetailWorkflowProps> = ({
  documentId,
  documentWorkflowStatus,
  workflowAvailableActions,
  workflowStatuses,
  workflowStatusId,
  workflowHistory = [],
  onWorkflowAction,
  showStepperModal: showStepperModalProp,
  setShowStepperModal: setShowStepperModalProp,
  onClaim,
  onUnclaim,
  claimedById,
  isClaimedByMe,
  claimSubmitting,
  userCanEdit,
  claimedByUsername,
}) => {
  const { t } = useTranslation()
  const [showStepperModalLocal, setShowStepperModalLocal] = useState(false)
  const [completedWorkflowName, setCompletedWorkflowName] = useState<string | null>(null)
  /** When set, opening the stepper modal will preselect this action (show comment form). */
  const [openWithActionId, setOpenWithActionId] = useState<number | null>(null)
  const showStepperModal = setShowStepperModalProp != null ? (showStepperModalProp ?? false) : showStepperModalLocal
  const setShowStepperModal = (show: boolean) => {
    if (setShowStepperModalProp) setShowStepperModalProp(show)
    else setShowStepperModalLocal(show)
    if (!show) setOpenWithActionId(null)
  }

  useEffect(() => {
    if (workflowAvailableActions?.in_workflow) {
      setCompletedWorkflowName(null)
      return
    }
    if (!workflowStatusId || !workflowHistory?.length) {
      setCompletedWorkflowName(null)
      return
    }
    /** Use name from history payload to avoid admin definition GET (403 for non-admin users). */
    const statusChangeEntry = workflowHistory.find((e) => e.entry_type === 'status_change' && e.workflow_definition_id != null)
    setCompletedWorkflowName(statusChangeEntry?.workflow_definition_name ?? null)
  }, [workflowAvailableActions?.in_workflow, workflowStatusId, workflowHistory])

  const workflowName = workflowAvailableActions?.workflow_definition?.name ?? completedWorkflowName ?? null

  const wsFromCatalog = workflowStatusId != null ? (workflowStatuses.find((s) => s.id === workflowStatusId) ?? null) : null
  const docMatchesId = documentWorkflowStatus != null && (workflowStatusId === null || documentWorkflowStatus.id === workflowStatusId)
  const wsFromDocument = docMatchesId ? documentWorkflowStatus : null
  const ws = wsFromDocument ?? wsFromCatalog

  const statusDisplay =
    ws != null ? (
      <span
        className="badge px-2 py-1 fw-normal align-middle"
        style={{
          backgroundColor: ws.color || '#6c757d',
          color: ws.text_color || '#fff',
        }}
      >
        {ws.name}
      </span>
    ) : (
      <span className="text-muted small">{t('document_workflow.workflow_name_none')}</span>
    )

  const showClaimSection =
    workflowAvailableActions?.in_workflow && workflowAvailableActions?.user_can_claim && (workflowAvailableActions?.has_multiple_assignees ?? false)

  /** Badge/summary is clickable when document has a workflow status (active or completed). */
  const showClickableSummary = workflowStatusId != null

  const workflowSummary = workflowName ? <span>{workflowName}</span> : <span className="text-muted">{t('document_workflow.workflow_name_none')}</span>

  const inWorkflow = workflowAvailableActions?.in_workflow ?? false
  const userCanPerformActions = workflowAvailableActions?.user_can_perform_actions ?? false
  const availableActions = workflowAvailableActions?.available_actions ?? []
  const assigneeUsernames = workflowAvailableActions?.current_step_assignee_usernames ?? []
  const assigneeGroupNames = workflowAvailableActions?.current_step_assignee_group_names ?? []
  const hasAssignees = assigneeUsernames.length > 0 || assigneeGroupNames.length > 0

  const latestComment = documentWorkflowHistoryService.getLatestWorkflowComment(workflowHistory)

  return (
    <>
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm={3} className="fw-medium small">
          {t('status')}
        </Form.Label>
        <Col sm={9} className="d-flex align-items-center flex-wrap">
          {statusDisplay}
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-3 align-items-start align-items-center">
        <Form.Label column sm={3} className="fw-medium">
          {t('document_workflow.workflow_name')}
        </Form.Label>
        <Col sm={9}>
          {showClickableSummary ? (
            <button
              type="button"
              className="btn btn-link p-0 text-start text-decoration-none d-inline-flex align-items-center flex-wrap gap-1"
              onClick={() => setShowStepperModal(true)}
              title={t('document_workflow.stepper_open_title')}
            >
              {workflowSummary}
            </button>
          ) : (
            workflowSummary
          )}
        </Col>
      </Form.Group>

      {latestComment && (
        <Form.Group as={Row} className="mb-3 align-items-center">
          <Form.Label column sm={3} className="fw-medium">
            {t('document_workflow.comment')}
          </Form.Label>
          <Col sm={9}>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {latestComment}
            </div>
          </Col>
        </Form.Group>
      )}

      {inWorkflow && userCanPerformActions && availableActions.length > 0 && (
        <Form.Group as={Row} className="mb-3 align-items-center">
          <Form.Label column sm={3} className="fw-medium">
            {t('document_workflow.actions')}
          </Form.Label>
          <Col sm={9}>
            <div className="d-flex flex-wrap gap-2">
              {availableActions.map((action) => (
                <Button
                  key={action.id}
                  size="sm"
                  variant={action.color ? undefined : action.end_workflow ? 'success' : 'primary'}
                  style={action.color ? { backgroundColor: action.color, borderColor: action.color, color: action.text_color ?? '#fff' } : undefined}
                  onClick={() => {
                    setOpenWithActionId(action.id)
                    setShowStepperModal(true)
                  }}
                >
                  {action.name}
                </Button>
              ))}
            </div>
          </Col>
        </Form.Group>
      )}

      {inWorkflow && hasAssignees && (
        <Form.Group as={Row} className="mb-3 align-items-center">
          <Form.Label column sm={3} className="fw-medium">
            {t('document_workflow.waiting_on')}
          </Form.Label>
          <Col sm={9}>
            <span className="text-muted small">{[...assigneeUsernames, ...assigneeGroupNames].join(', ')}</span>
          </Col>
        </Form.Group>
      )}

      {showStepperModal && documentId > 0 && (
        <DocumentWorkflowStepperModal
          show={showStepperModal}
          onHide={() => setShowStepperModal(false)}
          documentId={documentId}
          workflowStatuses={workflowStatuses}
          workflowStatusId={workflowStatusId}
          onWorkflowAction={onWorkflowAction}
          userCanEdit={userCanEdit}
          initialActionId={openWithActionId}
          onInitialActionConsumed={() => setOpenWithActionId(null)}
        />
      )}

      {showClaimSection && (
        <Form.Group as={Row} className="mb-3 align-items-center">
          <Form.Label column sm={3} className="fw-medium">
            {t('document_workflow.claim.label')}
          </Form.Label>
          <Col sm={9}>
            {!claimedById && (
              <Button variant="outline-secondary" size="sm" onClick={onClaim} disabled={claimSubmitting || !userCanEdit}>
                {claimSubmitting ? <Spinner animation="border" size="sm" /> : <i className="bi bi-person-check" />}
                &nbsp;{t('document_workflow.claim.claim_button')}
              </Button>
            )}
            {isClaimedByMe && (
              <>
                <span className="text-muted small me-2">{t('document_workflow.claim.claimed_by_you')}</span>
                <Button variant="outline-secondary" size="sm" onClick={onUnclaim} disabled={claimSubmitting}>
                  {claimSubmitting ? <Spinner animation="border" size="sm" /> : <i className="bi bi-person-x" />}
                  &nbsp;{t('document_workflow.claim.unclaim_button')}
                </Button>
              </>
            )}
            {claimedById != null && !isClaimedByMe && (
              <span className="text-muted small">
                {t('document_workflow.claim.claimed_by')}: {claimedByUsername ?? claimedById}
              </span>
            )}
          </Col>
        </Form.Group>
      )}
    </>
  )
}
